import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { dnAlert } from "./utils/dNews_Alert";
import { FaReceipt, FaTimes, FaPen, FaPlus, FaTrash, FaLock, FaTags } from "react-icons/fa";
import { apiFetch, DSLIP_FILE_BASE } from "./utils/api";
import { exportTicketsExcel, exportTicketsPdf } from "./utils/ticketExport";
import ExportMenu from "./ExportMenu";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./dSpr.css";
import "./Reimbursement_Admin.css";

// Status filter chips (order = the claim lifecycle). "All" is the default view.
const FILTER_STATUSES = [
    "All", "Applied", "Clarification Required", "Approved",
    "Payment in Process", "Reimbursed", "Rejected", "Resubmitted",
];

// Colour per status - reused for the badge and the filter chips.
const STATUS_COLOR = {
    "Applied":               "#64748b",
    "Clarification Required": "#e67e22",
    "Approved":              "#2ecc71",
    "Payment in Process":    "#3498db",
    "Reimbursed":            "#16a34a",
    "Rejected":              "#e74c3c",
    "Resubmitted":           "#9b59b6",
};

// The five actions an admin can take, in lifecycle order. Each row shows the
// ones that aren't its current status; terminal rows show none.
const ACTIONS = [
    { status: "Approved",               label: "Approve",  cls: "rb-a-approve" },
    { status: "Clarification Required", label: "Clarify",  cls: "rb-a-clarify" },
    { status: "Payment in Process",     label: "Payment",  cls: "rb-a-payment" },
    { status: "Reimbursed",             label: "Reimburse", cls: "rb-a-reimburse" },
    { status: "Rejected",               label: "Reject",   cls: "rb-a-reject" },
];
const TERMINAL = ["Reimbursed", "Rejected", "Resubmitted"];

const money = (v) =>
    "₹" + Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Reimbursement_Admin = ({ navSize }) => {
    const navigate = useNavigate();
    const [claims, setClaims] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("All");

    // Manage Types modal
    const [showTypes, setShowTypes] = useState(false);
    const [types, setTypes] = useState([]);
    const [newType, setNewType] = useState("");
    const [busyTypes, setBusyTypes] = useState(false);

    useEffect(() => {
        fetchloginedEmployees();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchClaims();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const fetchloginedEmployees = async () => {
        try {
            const res = await apiFetch(`/api/employee/logined_employee`, { method: "GET", credentials: "include" });
            const data = await res.json();
            if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") {
                navigate("/login");
            }
        } catch (err) {
            console.error("Error fetching employee:", err);
        }
    };

    const fetchClaims = async () => {
        setLoading(true);
        try {
            const qs = statusFilter === "All" ? "" : `?status=${encodeURIComponent(statusFilter)}`;
            const res = await apiFetch(`/api/reimbursement/list${qs}`);
            const data = await res.json();
            setClaims(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("❌ Error fetching claims:", err);
            setClaims([]);
        } finally {
            setLoading(false);
        }
    };

    // ── Export (Excel + PDF) via the shared ticket exporter ──────────────────
    const buildExport = () => {
        const columns = [
            { key: "emp",      label: "Employee",     width: 24, pdfWidth: 120 },
            { key: "emp_id",   label: "Emp ID",       width: 14, pdfWidth: 70 },
            { key: "type",     label: "Type",         width: 16, pdfWidth: 80 },
            { key: "occurred", label: "Occurred",     width: 14, pdfWidth: 75 },
            { key: "amount",   label: "Amount (INR)", width: 16, pdfWidth: 85 },
            { key: "applied",  label: "Applied",      width: 14, pdfWidth: 75 },
            { key: "status",   label: "Status",       width: 20, pdfWidth: 95 },
            { key: "details",  label: "Details",      width: 42, pdfWidth: 165, wrap: true },
            { key: "receipt",  label: "Receipt",      width: 30, pdfWidth: 130, wrap: true },
        ];
        const data = claims.map((c) => ({
            emp: `${c.emp_first_name || ""} ${c.emp_last_name || ""}`.trim() || c.emp_id,
            emp_id: c.emp_id,
            type: c.type_name || "-",
            occurred: c.occurrence_date || "-",
            amount: Number(c.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            applied: c.applied_date || "-",
            status: c.status,
            details: [
                c.emp_comments && `Comment: ${c.emp_comments}`,
                c.parent_id && c.parent_query && `Prior query: ${c.parent_query}`,
                c.emp_reply && `Emp reply: ${c.emp_reply}`,
            ].filter(Boolean).join("  |  ") || "-",
            receipt: c.file_name || "-",
        }));
        const filterLabel = statusFilter !== "All" ? `Status: ${statusFilter}` : "";
        return { columns, data, filterLabel };
    };
    const stampDate = () => new Date().toISOString().slice(0, 10);
    const exportExcel = () => { const { columns, data, filterLabel } = buildExport(); exportTicketsExcel({ moduleName: "dSlip", sheetTitle: "Reimbursements", columns, rows: data, filterLabel, filename: `reimbursements_${stampDate()}.xlsx` }); };
    const exportPdf = () => { const { columns, data, filterLabel } = buildExport(); exportTicketsPdf({ moduleName: "dSlip", sheetTitle: "Reimbursements", columns, rows: data, filterLabel, filename: `reimbursements_${stampDate()}.pdf` }); };

    // ── Status action ───────────────────────────────────────────────────────
    const act = async (claim, status) => {
        const who = `${claim.emp_first_name} ${claim.emp_last_name}`.trim() || claim.emp_id;
        let comment = "";

        if (status === "Rejected" || status === "Clarification Required") {
            const isReject = status === "Rejected";
            const val = await dnAlert.prompt({
                title: isReject ? "Reason for rejection" : "Clarification query",
                message: `This will be sent to ${who} and is required.`,
                placeholder: isReject ? "Why is this claim being rejected?" : "What clarification do you need from the employee?",
                confirmLabel: isReject ? "Reject" : "Send query",
                danger: isReject,
            });
            if (val === null || val === undefined) return;      // cancelled
            if (!String(val).trim()) {
                dnAlert.warning("A comment is required", `Please enter a ${isReject ? "reason" : "query"}.`);
                return;
            }
            comment = String(val).trim();
        } else {
            const ok = await dnAlert.confirm({
                title: `Mark as "${status}"?`,
                message: `${who}'s claim of ${money(claim.amount)} will be set to ${status}.`,
                confirmLabel: "Confirm",
            });
            if (!ok) return;
        }

        try {
            const res = await apiFetch(`/api/reimbursement/${claim.reimbursement_id}/status`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ status, comment }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Update failed");
            dnAlert.success("Updated", data.message || `Marked ${status}.`);
            fetchClaims();
        } catch (err) {
            dnAlert.error("Couldn't update", err.message);
        }
    };

    // ── Manage Types ──────────────────────────────────────────────────────────
    const openTypes = async () => {
        setShowTypes(true);
        loadTypes();
    };
    const loadTypes = async () => {
        try {
            const res = await apiFetch(`/api/reimbursement/types`);
            const data = await res.json();
            setTypes(Array.isArray(data) ? data : []);
        } catch { setTypes([]); }
    };
    const addType = async () => {
        const name = newType.trim();
        if (!name) { dnAlert.warning("Enter a type name", ""); return; }
        setBusyTypes(true);
        try {
            const res = await apiFetch(`/api/reimbursement/types`, {
                method: "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ type_name: name }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Add failed");
            setNewType("");
            loadTypes();
        } catch (err) { dnAlert.error("Add failed", err.message); }
        finally { setBusyTypes(false); }
    };
    const renameType = async (t) => {
        const val = await dnAlert.prompt({ title: "Rename type", placeholder: "New name", confirmLabel: "Save" });
        if (val === null || val === undefined || !String(val).trim() || String(val).trim() === t.type_name) return;
        await updateType(t.type_id, { type_name: String(val).trim() });
    };
    const deleteType = async (t) => {
        const ok = await dnAlert.confirm({
            title: `Delete "${t.type_name}"?`,
            message: `"${t.type_name}" will be removed from the type list. Claims already filed under it keep their record - it just won't be selectable for new claims.`,
            confirmLabel: "Delete", danger: true,
        });
        if (!ok) return;
        try {
            const res = await apiFetch(`/api/reimbursement/types/${t.type_id}`, { method: "DELETE", credentials: "include" });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Delete failed");
            dnAlert.success("Deleted", `"${t.type_name}" removed.`);
            loadTypes();
        } catch (err) { dnAlert.error("Couldn't delete type", err.message); }
    };
    const updateType = async (id, body) => {
        try {
            const res = await apiFetch(`/api/reimbursement/types/${id}`, {
                method: "PUT", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Update failed");
            loadTypes();
        } catch (err) { dnAlert.error("Couldn't update type", err.message); }
    };

    return (
        <div className="rb-container">
            <div className={`rb-dashboard ${navSize}`}>
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#">
                            <img src={logo_eagle} alt="dAdmin Logo" style={{ height: "50px", objectFit: "contain", marginLeft: "-20px" }} />
                        </a>
                        Reimbursements
                    </h2>
                    <p className="dspr-subtitle">Review employee reimbursement claims raised in dSlip and move them through the lifecycle - approve, request clarification, reject, mark payment in process, or reimburse.</p>
                </div>

                <div className="rb-content">
                    {/* Filter chips + Manage Types */}
                    <div className="rb-toolbar">
                        <div className="rb-chips">
                            {FILTER_STATUSES.map((s) => (
                                <button
                                    key={s}
                                    className={`rb-chip ${statusFilter === s ? "active" : ""}`}
                                    style={statusFilter === s ? { background: STATUS_COLOR[s] || "#334155", borderColor: STATUS_COLOR[s] || "#334155", color: "#fff" } : undefined}
                                    onClick={() => setStatusFilter(s)}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                        <div className="rb-toolbar-right">
                            <ExportMenu onExcel={exportExcel} onPdf={exportPdf} disabled={!claims.length} />
                            <button className="rb-types-btn" onClick={openTypes}>
                                <FaTags /> Manage Types
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <p className="loading-text">Loading claims...</p>
                    ) : (
                        <div className="table-wrapper">
                            <table className="rb-table">
                                <thead>
                                    <tr>
                                        <th>Employee</th>
                                        <th>Type</th>
                                        <th>Occurred</th>
                                        <th>Amount</th>
                                        <th>Applied</th>
                                        <th>Receipt</th>
                                        <th>Details</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {claims.length === 0 ? (
                                        <tr><td colSpan="9" style={{ textAlign: "center", padding: "24px" }}>No claims found.</td></tr>
                                    ) : (
                                        claims.map((c) => {
                                            const isTerminal = TERMINAL.includes(c.status);
                                            const receiptUrl = c.file_path
                                                ? `${DSLIP_FILE_BASE}/Emp_Reimbursement_Uploades/${encodeURIComponent(c.file_path)}`
                                                : null;
                                            return (
                                                <tr key={c.reimbursement_id}>
                                                    <td>
                                                        <div className="rb-emp-name">{`${c.emp_first_name} ${c.emp_last_name}`.trim()}</div>
                                                        <div className="rb-emp-id">{c.emp_id}</div>
                                                    </td>
                                                    <td>{c.type_name || "-"}</td>
                                                    <td>{c.occurrence_date || "-"}</td>
                                                    <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{money(c.amount)}</td>
                                                    <td>{c.applied_date || "-"}</td>
                                                    <td style={{ textAlign: "center" }}>
                                                        {receiptUrl ? (
                                                            <a href={receiptUrl} target="_blank" rel="noopener noreferrer"
                                                               title={c.file_name || "Open receipt"} className="rb-receipt">
                                                                <FaReceipt />
                                                            </a>
                                                        ) : (
                                                            <span style={{ color: "#999" }}>No File</span>
                                                        )}
                                                    </td>
                                                    <td className="rb-details">
                                                        {c.emp_comments && (
                                                            <div className="rb-detail-line" title={c.emp_comments}>
                                                                <span className="rb-detail-k">Comment:</span> {c.emp_comments}
                                                            </div>
                                                        )}
                                                        {/* Re-submission: show the original query it answers + the reply */}
                                                        {c.parent_id && c.parent_query && (
                                                            <div className="rb-detail-line rb-detail-query" title={c.parent_query}>
                                                                <span className="rb-detail-k">Prior query:</span> {c.parent_query}
                                                            </div>
                                                        )}
                                                        {c.emp_reply && (
                                                            <div className="rb-detail-line rb-detail-reply" title={c.emp_reply}>
                                                                <span className="rb-detail-k">Emp reply:</span> {c.emp_reply}
                                                            </div>
                                                        )}
                                                        {!c.emp_comments && !c.parent_query && !c.emp_reply && (
                                                            <span style={{ color: "#999" }}>-</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className="rb-badge" style={{ background: STATUS_COLOR[c.status] || "#64748b" }}>
                                                            {c.status}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {isTerminal ? (
                                                            <span style={{ color: "#999" }}>-</span>
                                                        ) : (
                                                            <div className="rb-actions">
                                                                {ACTIONS.filter((a) => a.status !== c.status).map((a) => (
                                                                    <button key={a.status} className={`rb-act ${a.cls}`} onClick={() => act(c, a.status)}>
                                                                        {a.label}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Manage Types modal ───────────────────────────────────────── */}
            {showTypes && (
                <div className="rb-modal-overlay" onClick={() => setShowTypes(false)}>
                    <div className="rb-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="rb-modal-head">
                            <h3>Manage Reimbursement Types</h3>
                            <button className="rb-modal-close" onClick={() => setShowTypes(false)}><FaTimes /></button>
                        </div>
                        <div className="rb-modal-body">
                            <div className="rb-type-add">
                                <input
                                    type="text" value={newType} maxLength={80}
                                    placeholder="Add a new type (e.g. Relocation)"
                                    onChange={(e) => setNewType(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === "Enter") addType(); }}
                                    disabled={busyTypes}
                                />
                                <button className="rb-type-add-btn" onClick={addType} disabled={busyTypes || !newType.trim()}>
                                    <FaPlus /> Add
                                </button>
                            </div>
                            <div className="rb-type-list">
                                {types.length === 0 ? (
                                    <div className="rb-type-empty">No types yet.</div>
                                ) : (
                                    types.map((t) => {
                                        const isOthers = String(t.type_name).trim().toLowerCase() === "others";
                                        return (
                                        <div key={t.type_id} className="rb-type-row">
                                            <span className="rb-type-name">{t.type_name}</span>
                                            {isOthers && <span className="rb-type-tag rb-type-tag--default">default</span>}
                                            <div className="rb-type-actions">
                                                {isOthers ? (
                                                    <FaLock color="#94a3b8" title="Default type - can't be renamed or deleted" style={{ fontSize: 13 }} />
                                                ) : (
                                                    <>
                                                        <button className="rb-type-icon" title="Rename" onClick={() => renameType(t)}><FaPen /></button>
                                                        <button className="rb-type-icon" title="Delete" onClick={() => deleteType(t)}><FaTrash color="#e74c3c" /></button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Reimbursement_Admin;
